import { useUIStore } from 'store/uiStore';

export interface UseLoadingOptions {
  operationId?: string;
  message?: string;
  type?: 'overlay' | 'inline' | 'button';
}

export const useLoading = (options: UseLoadingOptions = {}) => {
  const { 
    setLoading, 
    setOperationLoading, 
    clearOperationLoading, 
    clearAllLoading,
    loading,
    loadingOperations 
  } = useUIStore();

  const {
    operationId,
    message = 'Loading...',
    type = 'overlay'
  } = options;

  const setLoadingState = (isLoading: boolean, customMessage?: string, customType?: 'overlay' | 'inline' | 'button') => {
    const finalMessage = customMessage || message;
    const finalType = customType || type;

    if (operationId) {
      setOperationLoading(operationId, isLoading, finalMessage, finalType);
    } else {
      setLoading(isLoading, finalMessage, finalType);
    }
  };

  const startLoading = (customMessage?: string, customType?: 'overlay' | 'inline' | 'button') => {
    setLoadingState(true, customMessage, customType);
  };

  const stopLoading = () => {
    setLoadingState(false);
  };

  const isLoading = operationId 
    ? loadingOperations.get(operationId)?.isLoading || false
    : loading.isLoading;

  const loadingMessage = operationId 
    ? loadingOperations.get(operationId)?.loadingMessage || message
    : loading.loadingMessage || message;

  const loadingType = operationId 
    ? loadingOperations.get(operationId)?.loadingType || type
    : loading.loadingType || type;

  return {
    isLoading,
    loadingMessage,
    loadingType,
    startLoading,
    stopLoading,
    setLoading: setLoadingState,
    clearLoading: () => {
      if (operationId) {
        clearOperationLoading(operationId);
      } else {
        setLoading(false);
      }
    },
    clearAllLoading,
  };
};

// Hook for API calls with automatic loading management
export const useApiCall = <T = any>(
  apiCall: () => Promise<T>,
  options: UseLoadingOptions = {}
) => {
  const { startLoading, stopLoading, isLoading } = useLoading(options);

  const execute = async (customMessage?: string): Promise<T> => {
    try {
      startLoading(customMessage);
      const result = await apiCall();
      return result;
    } finally {
      stopLoading();
    }
  };

  return {
    execute,
    isLoading,
  };
};
